import { useState, useEffect } from 'react';
import request from '@/api/request';

export interface PlatformSettings {
  platformName: string;
  platformLogo: string;
  platformDescription: string;
  miniProgramQrcode: string;
  icpNumber: string;
  psbNumber: string;
  icpLogo: string;
  psbLogo: string;
  contactPhone: string;
  contactEmail: string;
  contactAddress: string;
  publicContactVisible: boolean;
}

const DEFAULT_PLATFORM_NAME = '惠州跨区域校企协作平台';
const DOCUMENT_TITLE_SUFFIX = '门户端';
const CACHE_KEY = 'platform_settings_v6';
const CACHE_TTL = 1000 * 60 * 10;
const LEGACY_PLATFORM_TEXT = new Set([['惠州', '就业', '服务网'].join('')]);

const normalizePlatformText = (value?: string) => {
  const text = String(value || '').trim();
  return LEGACY_PLATFORM_TEXT.has(text) ? '' : text;
};

const fallback: PlatformSettings = {
  platformName: DEFAULT_PLATFORM_NAME,
  platformLogo: '',
  platformDescription: '',
  miniProgramQrcode: '',
  icpNumber: '',
  psbNumber: '',
  icpLogo: '',
  psbLogo: '',
  contactPhone: '',
  contactEmail: '',
  contactAddress: '',
  publicContactVisible: false,
};

export function usePlatformSettings(): PlatformSettings {
  const [settings, setSettings] = useState<PlatformSettings>(() => {
    const cached = localStorage.getItem(CACHE_KEY);
    if (cached) {
      try {
        const { data, time } = JSON.parse(cached);
        if (Date.now() - time < CACHE_TTL) return data;
      } catch { /* ignore */ }
    }
    return fallback;
  });

  useEffect(() => {
    request.get('/platform/settings/public')
      .then((res: any) => {
        const mapped: PlatformSettings = {
          platformName: normalizePlatformText(res?.platform_name) || fallback.platformName,
          platformLogo: res?.platform_logo || '',
          platformDescription: normalizePlatformText(res?.platform_description),
          miniProgramQrcode: res?.mini_program_qrcode || res?.wechat_qrcode || res?.app_qrcode || '',
          icpNumber: res?.icp_number || '',
          psbNumber: res?.psb_number || '',
          icpLogo: res?.icp_logo || '',
          psbLogo: res?.psb_logo || '',
          contactPhone: res?.contact_phone || '',
          contactEmail: res?.contact_email || '',
          contactAddress: res?.contact_address || '',
          publicContactVisible: String(res?.public_contact_visible || '').toLowerCase() === 'true',
        };
        localStorage.setItem(CACHE_KEY, JSON.stringify({ data: mapped, time: Date.now() }));
        setSettings(mapped);
      })
      .catch(() => { /* keep fallback */ });
  }, []);

  useEffect(() => {
    const name = settings.platformName || DEFAULT_PLATFORM_NAME;
    document.title = `${name} - ${DOCUMENT_TITLE_SUFFIX}`;
  }, [settings.platformName]);

  return settings;
}

export function clearPlatformSettingsCache() {
  localStorage.removeItem('platform_settings_v1');
  localStorage.removeItem('platform_settings_v2');
  localStorage.removeItem('platform_settings_v3');
  localStorage.removeItem('platform_settings_v4');
  localStorage.removeItem('platform_settings_v5');
  localStorage.removeItem(CACHE_KEY);
}
